import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LilDragonsCountingTabContent } from "./LilDragonsCountingTabContent";
import { LilDragonsHoShinSulTabContent } from "./LilDragonsHoShinSulTabContent";
import { LilDragonsOneStepTabContent } from "./LilDragonsOneStepTabContent";
import { LilDragonsPatternsTabContent } from "./LilDragonsPatternsTabContent";
import { LilDragonsInnaeTabContent } from "./LilDragonsInnaeTabContent";
import { LilDragonsKickingTabContent } from "./LilDragonsKickingTabContent";
import { LilDragonsMooshimTabContent } from "./LilDragonsMooshimTabContent";
import { LilDragonsSparringTabContent } from "./LilDragonsSparringTabContent";
import { LilDragonsProgressionTabContent } from "./LilDragonsProgressionTabContent";
import { scheduleScrollHorizontalStripToActiveTab } from "@/lib/scroll-curriculum-tab-strip";
import { cn } from "@/lib/utils";
import { ChevronsLeft, ChevronsRight } from "lucide-react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type RefObject,
} from "react";

interface LilDragonsTabProps {
  hasTrainingVideo: (aliases: string[]) => boolean;
  handleWatchTrainingVideo: (aliases: string[]) => void;
}

/** Same inactive tab surface as curriculum “Select Subject” (white belt). */
const LIL_DRAGONS_TAB_SURFACE = "!bg-white !text-gray-900";

const LIL_DRAGONS_SECTIONS = [
  { value: "creed", label: "Creed", mobileLabel: "Creed" },
  { value: "skills", label: "Skills", mobileLabel: "Skills" },
  { value: "progression", label: "Progression", mobileLabel: "Progress" },
  { value: "counting", label: "Counting", mobileLabel: "Counting" },
  { value: "mooshim", label: "Mooshim", mobileLabel: "Mooshim" },
  { value: "innae", label: "Innae", mobileLabel: "Innae" },
  { value: "kicking", label: "Kicking", mobileLabel: "Kicking" },
  { value: "sparring", label: "Sparring", mobileLabel: "Sparring" },
  { value: "ho-shin-sul", label: "Ho Shin Sul", mobileLabel: "Ho Shin Sul" },
  { value: "one-step", label: "One Step", mobileLabel: "One Step" },
  { value: "patterns", label: "Patterns", mobileLabel: "Patterns" },
] as const;

export default function LilDragonsTab({
  hasTrainingVideo,
  handleWatchTrainingVideo,
}: LilDragonsTabProps) {
  const [activeSection, setActiveSection] =
    useState<(typeof LIL_DRAGONS_SECTIONS)[number]["value"]>("creed");
  const lilDragonsNavScrollRef = useRef<HTMLDivElement | null>(null);
  const [showLilDragonsNavLeftHint, setShowLilDragonsNavLeftHint] = useState(false);
  const [showLilDragonsNavRightHint, setShowLilDragonsNavRightHint] = useState(false);

  const updateLilDragonsNavOverflowHints = useCallback(() => {
    const container = lilDragonsNavScrollRef.current;
    if (!container) return;

    const { scrollLeft, clientWidth, scrollWidth } = container;
    const maxScrollLeft = Math.max(0, scrollWidth - clientWidth);
    if (scrollLeft > maxScrollLeft) {
      container.scrollLeft = maxScrollLeft;
    }

    setShowLilDragonsNavLeftHint(container.scrollLeft > 4);
    setShowLilDragonsNavRightHint(maxScrollLeft - container.scrollLeft > 4);
  }, []);

  const scrollHorizontalTabStrip = useCallback(
    (stripRef: RefObject<HTMLDivElement | null>, direction: "backward" | "forward") => {
      const el = stripRef.current;
      if (!el) return;
      const delta = Math.max(160, Math.round(el.clientWidth * 0.65));
      const maxScrollLeft = Math.max(0, el.scrollWidth - el.clientWidth);
      const nextLeft =
        direction === "backward"
          ? Math.max(0, el.scrollLeft - delta)
          : Math.min(maxScrollLeft, el.scrollLeft + delta);
      el.scrollTo({ left: nextLeft, behavior: "smooth" });
    },
    [],
  );

  const scrollActiveLilDragonsSubjectIntoView = useCallback(() => {
    scheduleScrollHorizontalStripToActiveTab(
      lilDragonsNavScrollRef.current,
      '[data-lil-dragons-subject-tab][data-state="active"]',
      () => updateLilDragonsNavOverflowHints(),
    );
  }, [updateLilDragonsNavOverflowHints]);

  useEffect(() => {
    updateLilDragonsNavOverflowHints();
    const handleResize = () => updateLilDragonsNavOverflowHints();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [activeSection, updateLilDragonsNavOverflowHints]);

  useLayoutEffect(() => {
    updateLilDragonsNavOverflowHints();
    requestAnimationFrame(() => updateLilDragonsNavOverflowHints());
  }, [activeSection, updateLilDragonsNavOverflowHints]);

  useEffect(() => {
    scrollActiveLilDragonsSubjectIntoView();
  }, [activeSection, scrollActiveLilDragonsSubjectIntoView]);

  return (
    
     <div className="p-4 md:p-6">
  
                
                <div className="flex justify-center mb-6">
                  <img
                    src="/images/dragons/flat_text.png"
                    alt="Lil' Dragon"
                    className="max-w-full h-auto"
                    loading="lazy"
                    decoding="async"
                  />
                </div>

                <Tabs
                  value={activeSection}
                  onValueChange={(v) =>
                    setActiveSection(v as (typeof LIL_DRAGONS_SECTIONS)[number]["value"])
                  }
                  className="w-full space-y-4 mb-8"
                >
                  <div className="relative border-b bg-white/95 backdrop-blur md:border-0 md:bg-transparent">
                    <div
                      ref={lilDragonsNavScrollRef}
                      onScroll={updateLilDragonsNavOverflowHints}
                      className="touch-pan-x overflow-x-auto px-2 py-3 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
                    >
                      <TabsList className="inline-flex h-auto w-max min-w-0 flex-nowrap justify-start gap-2 rounded-full bg-gray-100 p-1">
                        {LIL_DRAGONS_SECTIONS.map((c) => (
                          <TabsTrigger
                            key={c.value}
                            value={c.value}
                            data-lil-dragons-subject-tab
                            className={cn(
                              "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium border border-black/10 transition-shadow",
                              LIL_DRAGONS_TAB_SURFACE,
                              "data-[state=active]:z-10 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-gray-900/40 data-[state=active]:ring-offset-2 data-[state=active]:ring-offset-gray-100",
                            )}
                          >
                            <span className="md:hidden">{c.mobileLabel}</span>
                            <span className="hidden md:inline">{c.label}</span>
                          </TabsTrigger>
                        ))}
                      </TabsList>
                    </div>
                    {showLilDragonsNavLeftHint && (
                      <div className="pointer-events-none absolute inset-y-0 left-0 z-30 flex w-14 items-center justify-start bg-gradient-to-r from-white via-white/90 to-transparent pl-2">
                        <button
                          type="button"
                          className="pointer-events-auto flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white/95 shadow-sm ring-1 ring-white/70 transition hover:bg-white focus-visible:outline focus-visible:ring-2 focus-visible:ring-gray-400"
                          aria-label="Scroll Lil Dragons tabs left"
                          onClick={() =>
                            scrollHorizontalTabStrip(lilDragonsNavScrollRef, "backward")
                          }
                        >
                          <ChevronsLeft className="h-5 w-5 text-gray-500" />
                        </button>
                      </div>
                    )}
                    {showLilDragonsNavRightHint && (
                      <div className="pointer-events-none absolute inset-y-0 right-0 z-30 flex w-14 items-center justify-end bg-gradient-to-l from-white via-white/90 to-transparent pr-2">
                        <button
                          type="button"
                          className="pointer-events-auto flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white/95 shadow-sm ring-1 ring-white/70 transition hover:bg-white focus-visible:outline focus-visible:ring-2 focus-visible:ring-gray-400"
                          aria-label="Scroll Lil Dragons tabs right"
                          onClick={() =>
                            scrollHorizontalTabStrip(lilDragonsNavScrollRef, "forward")
                          }
                        >
                          <ChevronsRight className="h-5 w-5 text-gray-500" />
                        </button>
                      </div>
                    )}
                  </div>

                  {LIL_DRAGONS_SECTIONS.map((c) => (
                    <TabsContent
                      key={c.value}
                      value={c.value}
                      className="mt-0 focus-visible:outline-none"
                    >
                      {c.value === "creed" ? (
                        <div className="flex flex-col md:flex-row items-center gap-8">
                          <div className="order-2 md:order-1 md:w-1/2">
                            <div className="bg-gray-50 rounded-lg p-4 md:p-6 shadow-sm">
                              <h3 className="text-lg font-semibold text-taekwondo-red mb-3">
                                Lil&apos; Dragon Creed
                              </h3>
                              <p className="text-sm text-gray-700 whitespace-pre-line">
                                {`I'm a little dragon, oh so strong,
Learning each day, what's right & wrong.
During this class, I know I'll find,
Honesty for my heart, and knowledge for my mind.
Dressed in purple, green & white,
This class will teach me left from right.`}
                              </p>
                            </div>
                          </div>
                          <div className="order-1 md:order-2 md:w-1/2 flex justify-center">
                            <img
                              src="/images/dragons/dragon.png"
                              alt="Lil' Dragon character"
                              className="max-w-[15rem] w-full h-auto"
                              loading="lazy"
                              decoding="async"
                            />
                          </div>
                        </div>
                      ) : c.value === "skills" ? (
                        <div className="flex flex-col md:flex-row items-center gap-8">
                          <div className="md:w-1/2 flex justify-center">
                            <img
                              src="/images/dragons/dragon_cert.png"
                              alt="Lil' Dragon motor skills"
                              className="max-w-[15rem] w-full h-auto"
                              loading="lazy"
                              decoding="async"
                            />
                          </div>
                          <div className="md:w-1/2">
                            <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm text-left text-sm text-gray-700">
                              <h3 className="text-lg font-semibold text-taekwondo-red mb-3">
                                Motor Skills
                              </h3>
                              <p className="mb-3">
                                Each week in our Lil&apos; Dragons programme, we focus on a different motor skill to help
                                build strong bodies, sharp minds, and good habits. Dragons work hard all term to earn
                                special tags that show they have mastered each skill.
                              </p>
                              <div className="space-y-2 text-sm mb-3">
                                <div className="flex flex-col sm:flex-row gap-2">
                                  <div
                                    className="flex-1 rounded-md px-3 py-2 flex items-center justify-between"
                                    style={{ backgroundColor: "#cc2027", color: "#FFFFFF" }}
                                  >
                                    <span className="font-semibold">Week 1</span>
                                    <span className="text-xs md:text-sm">Focus</span>
                                  </div>
                                  <div
                                    className="flex-1 rounded-md px-3 py-2 flex items-center justify-between"
                                    style={{ backgroundColor: "#4229e5", color: "#FFFFFF" }}
                                  >
                                    <span className="font-semibold">Week 2</span>
                                    <span className="text-xs md:text-sm">Teamwork</span>
                                  </div>
                                </div>
                                <div className="flex flex-col sm:flex-row gap-2">
                                  <div
                                    className="flex-1 rounded-md px-3 py-2 flex items-center justify-between"
                                    style={{ backgroundColor: "#15c432", color: "#FFFFFF" }}
                                  >
                                    <span className="font-semibold">Week 3</span>
                                    <span className="text-xs md:text-sm">Control</span>
                                  </div>
                                  <div
                                    className="flex-1 rounded-md px-3 py-2 flex items-center justify-between"
                                    style={{ backgroundColor: "#FFFF04", color: "#1F2933" }}
                                  >
                                    <span className="font-semibold">Week 4</span>
                                    <span className="text-xs md:text-sm">Balance</span>
                                  </div>
                                </div>
                                <div className="flex flex-col sm:flex-row gap-2">
                                  <div
                                    className="flex-1 rounded-md px-3 py-2 flex items-center justify-between"
                                    style={{ backgroundColor: "#F6941C", color: "#FFFFFF" }}
                                  >
                                    <span className="font-semibold">Week 5</span>
                                    <span className="text-xs md:text-sm">Memory</span>
                                  </div>
                                  <div
                                    className="flex-1 rounded-md px-3 py-2 flex items-center justify-between"
                                    style={{ backgroundColor: "#FFFFFF", color: "#1F2933" }}
                                  >
                                    <span className="font-semibold">Week 6</span>
                                    <span className="text-xs md:text-sm">Discipline</span>
                                  </div>
                                </div>
                                <div className="flex flex-col sm:flex-row gap-2">
                                  <div
                                    className="flex-1 rounded-md px-3 py-2 flex items-center justify-between"
                                    style={{ backgroundColor: "#000000", color: "#FFFFFF" }}
                                  >
                                    <span className="font-semibold">Week 7</span>
                                    <span className="text-xs md:text-sm">Fitness</span>
                                  </div>
                                  <div
                                    className="flex-1 rounded-md px-3 py-2 flex items-center justify-between"
                                    style={{ backgroundColor: "#a88428", color: "#FFFFFF" }}
                                  >
                                    <span className="font-semibold">Week 8</span>
                                    <span className="text-xs md:text-sm">Coordination</span>
                                  </div>
                                </div>
                              </div>
                              <p>
                                The tag will be awarded in their 2nd Class of the week. When a Lil&apos; Dragon has
                                collected all <span className="font-semibold">8 motor skill tags</span>, they are ready
                                to take part in grading.
                              </p>
                            </div>
                          </div>
                        </div>
                      ) : c.value === "progression" ? (
                        <LilDragonsProgressionTabContent />
                      ) : c.value === "counting" ? (
                        <LilDragonsCountingTabContent />
                      ) : c.value === "mooshim" ? (
                        <LilDragonsMooshimTabContent
                          hasTrainingVideo={hasTrainingVideo}
                          handleWatchTrainingVideo={handleWatchTrainingVideo}
                        />
                      ) : c.value === "innae" ? (
                        <LilDragonsInnaeTabContent
                          hasTrainingVideo={hasTrainingVideo}
                          handleWatchTrainingVideo={handleWatchTrainingVideo}
                        />
                      ) : c.value === "kicking" ? (
                        <LilDragonsKickingTabContent />
                      ) : c.value === "sparring" ? (
                        <LilDragonsSparringTabContent />
                      ) : c.value === "ho-shin-sul" ? (
                        <LilDragonsHoShinSulTabContent
                          hasTrainingVideo={hasTrainingVideo}
                          handleWatchTrainingVideo={handleWatchTrainingVideo}
                        />
                      ) : c.value === "one-step" ? (
                        <LilDragonsOneStepTabContent
                          hasTrainingVideo={hasTrainingVideo}
                          handleWatchTrainingVideo={handleWatchTrainingVideo}
                        />
                      ) : c.value === "patterns" ? (
                        <LilDragonsPatternsTabContent
                          hasTrainingVideo={hasTrainingVideo}
                          handleWatchTrainingVideo={handleWatchTrainingVideo}
                        />
                      ) : null}
                    </TabsContent>
                  ))}
                </Tabs>
    </div>
  );
}


